import { TableTextCell } from '@/components/ui/DataTable/DataTableCellExpansion'
import { JsonTableCell } from '@/components/ui/DataTable/JsonTableCell'
import { SortingHeaderButton } from '@/components/ui/DataTable/SortingHeaderButton'
import { RestSchemas } from '@/contexts/QueryProvider'
import { ColumnDef } from '@tanstack/react-table'
import { format as formatDate, isValid, parseISO } from 'date-fns'
import { RowActionsMenu } from './RowActionsMenu'
import { ScheduleEnabledSwitch } from './ScheduleEnabledSwitch'

type WorkspaceSchedule = RestSchemas['Messages.Scheduler.ExistingSchedule']

function formatDateTime(value?: string | null) {
  if (!value) return 'Not set'

  const parsed = parseISO(value)
  if (!isValid(parsed)) return value

  return formatDate(parsed, 'PP p')
}

export const workspaceEventSchedulerColumns: ColumnDef<WorkspaceSchedule>[] = [
  {
    id: 'description',
    accessorKey: 'description',
    header: ({ column }) => <SortingHeaderButton column={column}>Description</SortingHeaderButton>,
    cell: ({ row, cell }) => (
      <TableTextCell cellId={cell.id} value={row.original.description ?? 'Not set'} />
    ),
    sortingFn: (rowA, rowB) =>
      (rowA.original.description ?? '').localeCompare(rowB.original.description ?? ''),
  },
  {
    id: 'cronExpression',
    accessorKey: 'cronExpression',
    header: ({ column }) => <SortingHeaderButton column={column}>Cron expression</SortingHeaderButton>,
    cell: ({ row, cell }) => (
      <TableTextCell cellId={cell.id} value={row.original.cronExpression ?? 'Not set'} />
    ),
    sortingFn: (rowA, rowB) =>
      (rowA.original.cronExpression ?? '').localeCompare(rowB.original.cronExpression ?? ''),
  },
  {
    id: 'contentId',
    accessorKey: 'contentId',
    header: ({ column }) => <SortingHeaderButton column={column}>Content id</SortingHeaderButton>,
    cell: ({ row, cell }) => (
      <TableTextCell cellId={cell.id} value={row.original.contentId ?? 'Not set'} />
    ),
    sortingFn: (rowA, rowB) => (rowA.original.contentId ?? '').localeCompare(rowB.original.contentId ?? ''),
  },
  {
    id: 'filters',
    accessorKey: 'filters',
    header: 'Filters',
    cell: ({ row }) => <JsonTableCell value={row.original.filters} />,
    enableSorting: false,
    size: 200,
    minSize: 160,
    meta: { disableCellTruncate: true },
  },
  {
    id: 'payload',
    accessorKey: 'payload',
    header: 'Payload',
    cell: ({ row }) => <JsonTableCell value={row.original.payload} />,
    enableSorting: false,
    size: 200,
    minSize: 160,
    meta: { disableCellTruncate: true },
  },
  {
    id: 'nextRun',
    accessorKey: 'nextRun',
    header: ({ column }) => <SortingHeaderButton column={column}>Next run</SortingHeaderButton>,
    cell: ({ row, cell }) => (
      <TableTextCell cellId={cell.id} value={formatDateTime(row.original.nextRun)} />
    ),
    sortingFn: (rowA, rowB) => (rowA.original.nextRun ?? '').localeCompare(rowB.original.nextRun ?? ''),
    size: 140,
    minSize: 120,
  },
  {
    id: 'createdBy',
    accessorKey: 'createdBy',
    header: ({ column }) => <SortingHeaderButton column={column}>Created by</SortingHeaderButton>,
    cell: ({ row, cell }) => (
      <TableTextCell
        cellId={cell.id}
        value={row.original.createdBy != null ? String(row.original.createdBy) : 'Not set'}
      />
    ),
    sortingFn: (rowA, rowB) => (rowA.original.createdBy ?? 0) - (rowB.original.createdBy ?? 0),
  },
  {
    id: 'createdAt',
    accessorKey: 'createdAt',
    header: ({ column }) => <SortingHeaderButton column={column}>Created at</SortingHeaderButton>,
    cell: ({ row, cell }) => (
      <TableTextCell cellId={cell.id} value={formatDateTime(row.original.createdAt)} />
    ),
    sortingFn: (rowA, rowB) => (rowA.original.createdAt ?? '').localeCompare(rowB.original.createdAt ?? ''),
    size: 140,
    minSize: 120,
  },
  {
    id: 'enabled',
    accessorKey: 'enabled',
    header: ({ column }) => <SortingHeaderButton column={column}>Enabled</SortingHeaderButton>,
    cell: ({ row }) => <ScheduleEnabledSwitch schedule={row.original} />,
    sortingFn: (rowA, rowB) => Number(rowB.original.enabled) - Number(rowA.original.enabled),
    size: 88,
    minSize: 72,
    meta: { disableCellTruncate: true },
  },
  {
    id: 'actions',
    cell: ({ row }) => (
      <div className="flex justify-end">
        <RowActionsMenu row={row.original} />
      </div>
    ),
    meta: { disableCellTruncate: true },
  },
]
